import React, { FC, useState } from 'react';
import { priceFormatter } from 'common/helpers/format.helper';
import { dateDifference, formatTimeAgo } from 'common/helpers/date.helper';
import {
  ETrackStoreOrderView,
  ITrackStoreOrderCollectionFilters,
  ITrackStoreOrderListItem,
} from 'entities/TrackStore/TrackStore.models';
import { STORE_ORDERS_LIMIT, ZOR_MAINTENANCE_TARGET_MINS } from 'entities/TrackStore/TrackStore.const';
import { trackStoreTransactionItFormatter } from 'entities/TrackStore/TrackStore.helper';

interface IComponentProps {
  view: ETrackStoreOrderView;
  orders: ITrackStoreOrderListItem[];
  loading: boolean;
  onLoadMore: (filters: Omit<ITrackStoreOrderCollectionFilters, 'storeCode' | 'location'>) => void;
  onOpenOrder: (transactionId: string) => void;
}

export const TrackStoreStoreOrders: FC<IComponentProps> = ({ view, orders, loading, onLoadMore, onOpenOrder }) => {
  const [offset, setOffset] = useState<number>(STORE_ORDERS_LIMIT);

  const isQueue = view === ETrackStoreOrderView.Queue;

  const handleLoadMore = (): void => {
    onLoadMore({ view, offset, limit: STORE_ORDERS_LIMIT });
    setOffset((prev) => prev + STORE_ORDERS_LIMIT);
  };

  return (
    <div className="scrollbar-y track-store-store-orders">
      {orders.map((item) => {
        const now = new Date().toISOString();
        const dateDiffMs = dateDifference(now, item.orderedAt);
        const dateDiffMin = dateDiffMs / (60 * 1000);
        const timeExceeded = isQueue && dateDiffMin > ZOR_MAINTENANCE_TARGET_MINS;

        return (
          <div key={item.transactionId} className="mb-3 track-store-store-orders-item">
            <div
              className="cursor-pointer track-store-store-orders-item-block"
              style={{ pointerEvents: loading ? 'none' : 'auto' }}
              onClick={() => onOpenOrder(item.transactionId)}
            >
              <div className="mb-3 flex flex-justify-between">
                <span className={`ff-medium fs-xxs ${timeExceeded ? 'color-red' : 'color-black'}`}>
                  {trackStoreTransactionItFormatter(item.transactionId)}
                </span>
                <span className="color-gray-1">⏵</span>
              </div>

              <div>
                <span
                  className="mr-3 track-store-store-orders-item-indicator"
                  style={{ background: timeExceeded ? '#ff4e4e' : '#5dca8b' }}
                />
                <span className={`ff-regular fs-xxxs ${timeExceeded ? 'color-red' : 'color-black'}`}>
                  {isQueue ? formatTimeAgo(item.orderedAt) : formatTimeAgo(item.servedAt ?? now)}
                </span>
              </div>
            </div>

            <div className="track-store-store-orders-item-divider" />

            <div className="track-store-store-orders-item-block">
              <div className="mb-3">
                <span className="ff-regular fs-xxxs color-gray">Store: </span>
                <span className="ff-regular fs-xxxs">{item.store}</span>
              </div>
              <div>
                <span className="ff-regular fs-xxxs color-gray">Total: </span>
                <span className="ff-regular fs-xxxs">{priceFormatter(item.total)}</span>
              </div>
            </div>
          </div>
        );
      })}

      {orders.length >= STORE_ORDERS_LIMIT && (
        <div
          className="ff-regular fs-xxs t-align-c cursor-pointer"
          style={{ pointerEvents: loading ? 'none' : 'auto' }}
          onClick={handleLoadMore}
        >
          Show more
        </div>
      )}
    </div>
  );
};
